import { Tabs, Button, Modal, Tooltip, AutoComplete } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ContentKB from './tab';
import { PlusOutlined } from '@ant-design/icons';
import Onboard from '../contentKB/onboard/index';

const KnowledgeBase = () => {
  const allKbData = useSelector((state) => state.allKb.data);
  const allFaceBots = useSelector((state) => state.allFaceBots.data);

  const [knowledgeBaseList, setKnowledgeBaseList] = useState([]);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState(null);
  const [facebotId, setFacebotId] = useState(null);  //Renamed to follow convention
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [searchText, setSearchText] = useState('');

  const getAllKb = useCallback(async() => {
    try {
      setLoading(true);

      if (allKbData && Array.isArray(allKbData)) {
        setKnowledgeBaseList(allKbData);

        //If no kb is selected and list is available set the first kb
        if (!selectedKnowledgeBaseId && allKbData.length > 0) {
          setSelectedKnowledgeBaseId(allKbData[0].kb_id);
        }
      } else {
        console.error("API allKbData data is not an array:", allKbData);
        setKnowledgeBaseList([]);
        setSelectedKnowledgeBaseId(null);
      }
    } catch (err) {
      console.error("Error fetching facebots:", err);
      setKnowledgeBaseList([]);
      setSelectedKnowledgeBaseId(null);
    } finally {
      setLoading(false);
    }
  }, [allKbData, selectedKnowledgeBaseId]); //dependencies of getAllKb

  useEffect(() => {
    getAllKb();
  }, [getAllKb]); // Call getAllKb inside useEffect

  //Effect to update facebotId whenever selectedKnowledgeBaseId is changed.
  useEffect(() => {
    if (selectedKnowledgeBaseId) {
      const extractedFacebotId = ExtractFaceBotIdFromKbId(selectedKnowledgeBaseId);
      setFacebotId(extractedFacebotId);
    } else {
      setFacebotId(null);
    }
  }, [selectedKnowledgeBaseId]);
  const handleAddClick = (e, kbId) => {
    e.stopPropagation(); // Prevent tab switch
    console.log('Add clicked for knowledge base:', kbId);
    setIsModalOpen(true);
  };
  const handleModalOk = () => {
    setIsModalOpen(false);
    setShowOnboardModal(true);
  };
  
  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  const handleTabChange = (activeKey) => {
    setSelectedKnowledgeBaseId(activeKey);
  };
  const handleSelect = (value) => {
    const selectedKb = knowledgeBaseList.find(
      kb => kb.kb_name === value
    );
    if (selectedKb) {
      setSelectedKnowledgeBaseId(selectedKb.kb_id);
    }
  };

  // Sort knowledgeBaseList alphabetically by trimmed kb_name (case-insensitive)
  const sortedKnowledgeBaseList = [...knowledgeBaseList].sort((a, b) =>
    ((a.kb_name || '').trim().toLowerCase()).localeCompare((b.kb_name || '').trim().toLowerCase())
  );

  const autoCompleteOptions = sortedKnowledgeBaseList.map(kb => ({
    value: String(kb.kb_id),
    label: kb.kb_name,
    displayName: kb.kb_name,
  }));

  const tabItems = [
    ...sortedKnowledgeBaseList.map((kb) => ({
      key: String(kb.kb_id),
      label: (
        <span style={{ fontSize: 14, fontWeight: 500 }}>
          {kb.kb_name}
        </span>
      ),
      children: (
        <ContentKB
          KB_ID={kb.kb_id}
          FACEBOT_ID={facebotId}
        />
      ),
    })),
    {
      key: 'add-tab',
      label: (
        <Tooltip title="Create a new Knowledge Base">
        <Button 
          type="text" 
          icon={<PlusOutlined />} 
          size="small"
          onClick={(e) => handleAddClick(e)}
        />
        </Tooltip>
      ),
      children: null,
    }
  ];

  const defaultActiveKey = selectedKnowledgeBaseId;

  const ExtractFaceBotIdFromKbId = (kbId) => {

    if (!allFaceBots || !Array.isArray(allFaceBots) || allFaceBots.length === 0) {
      return null;
    }

    for (const faceBot of allFaceBots) {
      if (faceBot && faceBot.kb_id && faceBot.kb_id.includes(kbId)) {
        return faceBot.facebot_id; // Return the facebot_id if a match is found
      }
    }

    return null;
  };


  return (
    <div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 16
            }}>
              <AutoComplete
                placeholder="Find Knowledge Base"
                allowClear
                style={{
                  width: 200,
                  marginRight: 16
                }}
                options={autoCompleteOptions}
                value={searchText}
                onChange={setSearchText}
                onSelect={(value) => {
                  setSelectedKnowledgeBaseId(String(value));
                  const selectedKb = sortedKnowledgeBaseList.find(kb => String(kb.kb_id) === String(value));
                  if (selectedKb) {
                    setSearchText(selectedKb.kb_name);
                  }
                }}
                filterOption={(inputValue, option) =>
                  option?.displayName?.toLowerCase().includes(inputValue.toLowerCase())
                }
                notFoundContent={<span style={{ color: '#888' }}>No results found</span>}
                optionLabelProp="label"
                dropdownRender={menu => menu}
              />
            </div>
        <Tabs
          activeKey={selectedKnowledgeBaseId}
          type="card"
          style={{ marginBottom: 32 }}
          items={tabItems}
          onChange={setSelectedKnowledgeBaseId}
        />
        <Modal
            title="Create New Knowledge Base"
            open={isModalOpen}
            onOk={handleModalOk}
            onCancel={handleModalCancel}
            okText="Yes"
            cancelText="No"
          >
            <p>Are you sure you want to create one more Knowledge Base?</p>
          </Modal>
          <Modal
            title="Create Knowledge Base"
            open={showOnboardModal}
            onCancel={() => setShowOnboardModal(false)}
            footer={null}
            width={1200}
            style={{ top: 20 }}
            bodyStyle={{ 
              maxHeight: '80vh', 
              overflow: 'auto',
              padding: 0 
            }}
          >
            <Onboard />
          </Modal>
        </>
      )}
    </div>
  );
};

export default KnowledgeBase;